'use client';

import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from '@radix-ui/react-icons';
import { cn } from '@wolfejs/ui/utils/classnames';
import * as React from 'react';
import { Button, type ButtonProps } from '../Button/Button';
import { UIVariantsExtra, type UIComponentProps } from '../types/UI';

const PaginationBase = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav role="navigation" aria-label="pagination" className={className} {...props} />
);
PaginationBase.displayName = 'PaginationBase';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => <ul ref={ref} className={cn('flex flex-row items-center', className)} {...props} />
) as React.ForwardRefExoticComponentExtended<UIComponentProps>;
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
)) as React.ForwardRefExoticComponentExtended<UIComponentProps>;
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  ButtonProps;
// &  React.ComponentProps<'a'>;

const PaginationLink = ({ isActive, ...props }: Omit<PaginationLinkProps, 'ref'>) => (
  <Button aria-current={isActive ? 'page' : undefined} variant={UIVariantsExtra.link} {...props} />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, children, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to previous page" size="default" className={cn('gap-1 pl-2.5', className)} {...props}>
    {children ?? (
      <>
        <ChevronLeftIcon className="h-4 w-4" />
        <span>Previous</span>
      </>
    )}
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, children, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to next page" size="default" className={cn('gap-1 pr-2.5', className)} {...props}>
    {children ?? (
      <>
        <span>Next</span>
        <ChevronRightIcon className="h-4 w-4" />
      </>
    )}
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span aria-hidden className={cn('flex h-9 w-9 items-center justify-center', className)} {...props}>
    <DotsHorizontalIcon className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  PaginationBase,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
